import { Component, OnInit, OnDestroy, AfterViewInit, signal, effect, EffectRef, inject, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet, Router } from '@angular/router';
import { Round } from '../../models';
import { TournamentService } from '../../services';
import { LoggingService } from '../../services/shared/logging/logging.service';
import { FormsModule } from '@angular/forms';
import { switchMap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { AlertContainerComponent } from '../shared/alert-container/alert-container.component';
import { AlertService } from '../../services/shared/alert/alert.service';
import { ScoringRealtimeService, RoundActivityData, RoundOfficialActivityData } from '../../services/signalr/scoring-realtime.service';
import { ConfigService } from '../../services/config/config.service';
import { ConfirmationDialogComponent } from '../shared/confirmation-dialog/confirmation-dialog.component';
import { ScorecardService } from '../../services/scorecard/scorecard.service';
import { AuditInfoComponent } from '../shared/audit-info/audit-info.component';

@Component({
  selector: 'app-rounds',
  standalone: true,
  imports: [CommonModule, FormsModule, AlertContainerComponent, RouterOutlet, ConfirmationDialogComponent, AuditInfoComponent],
  templateUrl: './rounds.component.html',
  styleUrls: ['./rounds.component.scss']
})
export class RoundsComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('startDateInput') startDateInput!: ElementRef<HTMLInputElement>;

  private loggingService = inject(LoggingService);
  private readonly statusBadgeClassMap: Record<string, string> = {
    'not started': 'badge badge-secondary',
    'in progress': 'badge badge-info',
    'complete': 'badge badge-success',
    'official': 'badge badge-primary'
  };

  // Create a property accessor for selectedRound that uses tournamentService
  get selectedRound() {
    return this.tournamentService.selectedRoundSignal;
  }

  // Local signal for rounds list
  rounds = signal<Round[]>([]);

  // Track dirty state for form changes
  isDirty = signal(false);
  isSaving = signal(false);

  // Store effect reference
  private tournamentEffectRef: EffectRef | null = null;

  // Round notification properties
  private roundEventsSubscription?: Subscription;
  private roundOfficialEventsSubscription?: Subscription;

  // Track local round operations to conditionally prevent showing notifications for locally performed operations
  // (only used when notifications.suppressLocalOperations is enabled in config)
  private localRoundOperations = new Map<string, number[]>();

  @ViewChild(ConfirmationDialogComponent) confirmationDialog!: ConfirmationDialogComponent;

  constructor(
    private readonly tournamentService: TournamentService,
    private readonly scorecardService: ScorecardService,
    private readonly toastr: ToastrService,
    private readonly alertService: AlertService,
    private readonly scoringRealtimeService: ScoringRealtimeService,
    private readonly configService: ConfigService,
    private readonly router: Router
  ) {
    // Set up effect to respond to tournament changes
    this.tournamentEffectRef = effect(() => {
      const tournament = this.tournamentService.selectedTournamentSignal();
      if (tournament?.tournamentId) {
        this.fetchRounds(tournament.tournamentId);
      }
    });
  }

  ngOnInit(): void {
    // Initial fetch if tournament is already selected
    const selectedTournament = this.tournamentService.selectedTournamentSignal();
    if (selectedTournament?.tournamentId) {
      this.fetchRounds(selectedTournament.tournamentId);
    }

    // Start listening to round events
    this.startListeningToRoundEvents();
  }

  ngAfterViewInit(): void {
    // Set focus to Start Date field after view is initialized
    setTimeout(() => {
      this.startDateInput?.nativeElement?.focus();
    }, 0);
  }

  // Clean up the effect when component is destroyed
  ngOnDestroy(): void {
    if (this.tournamentEffectRef) {
      this.tournamentEffectRef.destroy();
    }
    this.stopListeningToRoundEvents();
  }

  fetchRounds(tournamentId: string): void {
    this.tournamentService
      .getRounds(tournamentId)
      .subscribe((rounds: Round[]) => {
        this.rounds.set(rounds);

        // Reset dirty state when loading fresh data
        this.isDirty.set(false);

        // Auto-select the first round if no round is currently selected
        if (rounds.length > 0 && !this.selectedRound()) {
          this.selectRound(rounds[0]);
        }
      });
  }

  // Select a round
  selectRound(round: Round): void {
    this.tournamentService.selectedRound = round;
    // Reset dirty state when selecting a different round
    this.isDirty.set(false);
  }

  // Check if a round is the currently selected one
  isSelectedRound(round: Round): boolean {
    return this.selectedRound()?.roundId === round.roundId;
  }

  // Add a new round
  addRound(): void {
    const tournamentId = this.tournamentService.selectedTournamentSignal()?.tournamentId;
    if (!tournamentId) {
      this.toastr.error('Please select a tournament first', 'Error');
      return;
    }

    // Calculate the next round number
    const currentRounds = this.rounds();
    const nextRoundNumber = currentRounds.length > 0
      ? Math.max(...currentRounds.map(r => r.number || 0)) + 1
      : 1;

    // Show confirmation dialog
    this.confirmationDialog.open({
      title: 'Add Round',
      message: `Are you sure you want to add Round ${nextRoundNumber}?`,
      confirmButtonText: `Add Round`,
      cancelButtonText: 'Cancel',
      data: { type: 'addRound', tournamentId, nextRoundNumber }
    });
  }

  // Unified confirmation dialog handler
  onConfirmationDialogConfirmed(data: any): void {
    if (!data || !data.type) {
      return;
    }

    switch (data.type) {
      case 'addRound':
        this.onAddRoundConfirmed(data);
        break;
      case 'removeScores':
        this.onRemoveScoresConfirmed(data);
        break;
      case 'closeRound':
        this.onCloseRoundConfirmed(data);
        break;
      case 'removeRound':
        this.onRemoveRoundConfirmed(data);
        break;
      case 'toggleRoundActive':
        this.onToggleRoundActiveConfirmed(data);
        break;
      default:
        this.loggingService.warn('RoundsComponent', 'Unknown confirmation type:', data.type);
    }
  }

  // Handle confirmation of adding a round
  onAddRoundConfirmed(data: any): void {
    const { tournamentId, nextRoundNumber } = data;

    // Calculate start date based on the last round's end date
    const currentRounds = this.rounds();
    let startDate = new Date();

    if (currentRounds.length > 0) {
      // Sort rounds by number to get the last one
      const sortedRounds = [...currentRounds].sort((a, b) => (a.number || 0) - (b.number || 0));
      const lastRound = sortedRounds[sortedRounds.length - 1];

      if (lastRound.endDate) {
        // Set start date to be one day after the last round's end date
        const lastEndDate = new Date(lastRound.endDate);
        startDate = new Date(lastEndDate);
        startDate.setDate(startDate.getDate() + 1);
      }
    }

    // Create new round model
    const newRoundModel = {
      number: nextRoundNumber,
      startDate: startDate,
      endDate: startDate, // Set end date to same as start date initially
      active: false,
      official: false
    };

    // Call the API to create the round
    this.tournamentService.tournamentsTournamentIdRoundsPost(
      tournamentId,
      newRoundModel
    )
      .pipe(
        // After creating, re-fetch the rounds to get the updated data
        switchMap(() => {
          return this.tournamentService.getRounds(tournamentId);
        })
      )
      .subscribe({
        next: (updatedRounds: Round[]) => {
          // Update the rounds signal with the new data
          this.rounds.set(updatedRounds);

          // Find the newly created round and select it
          const newRound = updatedRounds.find(r => r.number === nextRoundNumber);
          if (newRound) {
            this.selectRound(newRound);
          }

          // Show success message
          this.toastr.success(`Round ${nextRoundNumber} created successfully`, 'Success');
        },
        error: (err: any) => {
          this.loggingService.error('Error creating round:', err);
          this.toastr.error(`Failed to create round: ${err.message || 'Unknown error'}`, 'Error');
        }
      });
  }

  // Confirm remove scores action
  confirmRemoveScores(): void {
    const round = this.selectedRound();
    if (!round) {
      this.toastr.error('Please select a round first', 'Error');
      return;
    }

    if (round.official) {
      this.toastr.warning('This round is official and scores cannot be removed.', 'Round Locked');
      return;
    }

    const roundNumber = round.number;

    // Show confirmation dialog
    this.confirmationDialog.open({
      title: 'Remove Scores',
      message: `Are you sure you want to remove all scores from Round ${roundNumber}? This action cannot be undone.`,
      confirmButtonText: 'Remove Scores',
      cancelButtonText: 'Cancel',
      dangerMode: true,
      data: { type: 'removeScores', roundId: round.roundId }
    });
  }

  // Handle confirmation of removing scores
  onRemoveScoresConfirmed(data: any): void {
    const roundId = data?.roundId;
    const tournamentId = this.tournamentService.selectedTournamentSignal()?.tournamentId;

    if (!roundId || !tournamentId) {
      this.toastr.error('Unable to determine tournament or round context', 'Error');
      this.loggingService.error('RoundsComponent', 'Remove scores failed due to missing identifiers', {
        roundId,
        tournamentId
      });
      return;
    }

    const roundNumber = this.selectedRound()?.number;
    this.loggingService.debug('RoundsComponent', `Removing all scores for round ${roundNumber ?? ''} (${roundId}) in tournament ${tournamentId}`);

    this.scorecardService.removeScoresForRound(tournamentId, roundId).subscribe({
      next: (response) => {
        const scoresRemoved = response?.scoresRemoved ?? 0;
        const scorecardsAffected = response?.scorecardsAffected ?? 0;

        this.scorecardService.refreshScorecardCounts(roundId);

        this.toastr.success(
          `Removed ${scoresRemoved} score${scoresRemoved === 1 ? '' : 's'} across ${scorecardsAffected} scorecard${scorecardsAffected === 1 ? '' : 's'}.`,
          'Scores Removed'
        );
        this.loggingService.debug('RoundsComponent', 'Remove scores completed', {
          scoresRemoved,
          scorecardsAffected
        });
      },
      error: (err: any) => {
        this.loggingService.error('RoundsComponent', 'Error removing scores', err);
        this.toastr.error(`Failed to remove scores: ${err?.message ?? 'Unknown error'}`, 'Error');
      }
    });
  }

  // Confirm close round action
  confirmCloseRound(): void {
    const round = this.selectedRound();
    if (!round) {
      this.toastr.error('Please select a round first', 'Error');
      return;
    }

    if (round.official) {
      this.toastr.warning('This round is already official.', 'Round Locked');
      return;
    }

    const roundNumber = round.number;

    // Show confirmation dialog
    this.confirmationDialog.open({
      title: 'Close Round',
      message: `Are you sure you want to close Round ${roundNumber}? Once closed, no further changes can be made to this round.`,
      confirmButtonText: 'Close Round',
      cancelButtonText: 'Cancel',
      dangerMode: true,
      data: { type: 'closeRound', roundId: round.roundId }
    });
  }

  // Handle confirmation of closing round
  onCloseRoundConfirmed(data: any): void {
    const selectedRound = this.selectedRound();
    const selectedTournament = this.tournamentService.selectedTournamentSignal();

    if (!selectedRound || !selectedTournament) {
      this.toastr.error('Please select a tournament and round first', 'Error');
      return;
    }

    this.loggingService.debug('RoundsComponent', 'Navigating to close round page for round:', selectedRound);
    // Navigate to the round close page
    this.router.navigate(['/round-close']);
  }

  // Confirm remove round action
  confirmRemoveRound(): void {
    if (!this.selectedRound()) {
      this.toastr.error('Please select a round first', 'Error');
      return;
    }

    const roundNumber = this.selectedRound()!.number;

    // Show confirmation dialog
    this.confirmationDialog.open({
      title: 'Remove Round from Tournament',
      message: `Are you sure you want to remove "<strong>Round ${roundNumber}</strong>" from this tournament?</em>`,
      confirmButtonText: 'Remove Round',
      cancelButtonText: 'Cancel',
      dangerMode: true,
      data: { type: 'removeRound', roundId: this.selectedRound()!.roundId }
    });
  }

  // Handle confirmation of removing round
  onRemoveRoundConfirmed(data: any): void {
    const roundId = data.roundId;
    const tournamentId = this.tournamentService.selectedTournamentSignal()?.tournamentId;

    if (!tournamentId || !roundId) {
      this.toastr.error('Invalid tournament or round ID', 'Error');
      return;
    }

    // Get the round number for the success message
    const round = this.rounds().find(r => r.roundId === roundId);
    const roundNumber = round?.number || 'Unknown';

    // Call the API to delete the round
    this.tournamentService.tournamentsTournamentIdRoundsRoundIdDelete(
      tournamentId,
      roundId
    )
      .pipe(
        // After deleting, re-fetch the rounds to get the updated data
        switchMap(() => {
          return this.tournamentService.getRounds(tournamentId);
        })
      )
      .subscribe({
        next: (updatedRounds: Round[]) => {
          // Update the rounds signal with the new data
          this.rounds.set(updatedRounds);

          // If the removed round was the selected round, select the first available round
          if (this.selectedRound()?.roundId === roundId) {
            if (updatedRounds.length > 0) {
              this.selectRound(updatedRounds[0]);
            } else {
              this.tournamentService.selectedRound = null;
            }
          }

          // Reset dirty state
          this.isDirty.set(false);

          // Show success message
          this.toastr.success(`Round ${roundNumber} removed successfully`, 'Success');
        },
        error: (err: any) => {
          this.loggingService.error('Error removing round:', err);
          this.toastr.error(`Failed to remove round: ${err.message || 'Unknown error'}`, 'Error');
        }
      });
  }

  formatDateForInput(date: Date | null): string {
    if (!date) return '';
    return new Date(date).toISOString().split('T')[0];
  }

  parseInputDate(dateString: string): Date {
    return new Date(dateString);
  }

  /**
   * Mark the form as dirty (has unsaved changes)
   */
  markDirty(): void {
    this.isDirty.set(true);
  }

  getStatusBadgeClass(status?: string | null): string {
    const normalized = status?.trim().toLowerCase() ?? '';
    return this.statusBadgeClassMap[normalized] ?? 'badge badge-light';
  }

  getStatusDisplayText(status?: string | null): string {
    return status && status.trim().length > 0 ? status : 'Unknown';
  }

  updateCutSize(value: string | number | null): void {
    const round = this.selectedRound();
    if (!round) {
      return;
    }

    const sanitizedInput = value === null || value === undefined ? '' : String(value).trim();

    if (sanitizedInput.length === 0) {
      if (round.cutSize !== undefined && round.cutSize !== null) {
        this.updateRoundProperty('cutSize', undefined);
      }

      if (round.cutType !== undefined && round.cutType !== null) {
        this.updateRoundProperty('cutType', undefined);
      }

      if (round.resetScores !== undefined && round.resetScores !== null) {
        this.updateRoundProperty('resetScores', undefined);
      }

      return;
    }

    const parsedValue = Number(sanitizedInput);
    if (Number.isNaN(parsedValue)) {
      return;
    }

    if (parsedValue <= 1 || parsedValue >= 500) {
      this.toastr.error('Cut size must be greater than 1 and less than 500.', 'Invalid Cut Size');
      return;
    }

    if (round.cutSize === parsedValue) {
      return;
    }

    this.updateRoundProperty('cutSize', parsedValue);
    if (
      round.cutType === undefined ||
      round.cutType === null ||
      round.cutType.trim().length === 0 ||
      round.resetScores === undefined ||
      round.resetScores === null
    ) {
      this.toastr.warning('Select cut type and reset scores before saving.', 'Cut Details Incomplete');
    }
  }

  updateCutType(value: string | null): void {
    const round = this.selectedRound();
    if (!round) {
      return;
    }

    const sanitizedInput = value === null || value === undefined ? '' : value.trim();

    if (sanitizedInput.length === 0) {
      if (round.cutType !== undefined && round.cutType !== null) {
        this.updateRoundProperty('cutType', undefined);
      }

      return;
    }

    if (round.cutType === sanitizedInput) {
      return;
    }

    this.updateRoundProperty('cutType', sanitizedInput);
    if (round.cutSize === undefined || round.cutSize === null) {
      this.toastr.warning('Enter a cut size before selecting cut type.', 'Missing Cut Size');
    }
  }

  setResetScores(value: boolean | null): void {
    const round = this.selectedRound();
    if (!round) {
      return;
    }

    if (round.cutSize === undefined || round.cutSize === null) {
      return;
    }

    const normalizedValue = value === null ? undefined : value;

    if (round.resetScores === normalizedValue) {
      return;
    }

    this.updateRoundProperty('resetScores', normalizedValue);

    if (round.cutType === undefined || round.cutType === null || round.cutType.trim().length === 0) {
      this.toastr.warning('Select a cut type before toggling reset scores.', 'Missing Cut Type');
    }
  }

  // Update a property of the selected round
  updateRoundProperty(property: string, value: any): void {
    if (!this.selectedRound()) return;

    // Get the current round from the rounds array to avoid working with the computed value
    const roundId = this.selectedRound()!.roundId;
    const updatedRounds = this.rounds().map(r => {
      if (r.roundId === roundId) {
        // Update the specific property
        if (property === 'cutSize' && value !== undefined && value !== null) {
          const numericValue = Number(value);
          if (Number.isNaN(numericValue) || numericValue <= 1 || numericValue >= 500) {
            this.toastr.error('Cut size must be greater than 1 and less than 500.', 'Invalid Cut Size');
            return r;
          }
        }

        r[property as keyof Round] = value;
      }
      return r;
    });

    // Update the rounds signal
    this.rounds.set(updatedRounds);

    // Also update the selected round in the service
    const updatedRound = updatedRounds.find(r => r.roundId === roundId);
    if (updatedRound) {
      this.tournamentService.selectedRound = updatedRound;
    }

    // Mark form as dirty
    this.markDirty();
  }

  // Toggle round active status
  toggleRoundActive(round: Round): void {
    if (!round.roundId) return;

    const newActiveState = !round.active;

    if (newActiveState) {
      this.confirmationDialog.open({
        title: 'Mark Round Active',
        message: `Are you sure you want to mark <strong>Round ${round.number}</strong> as active? This will deactivate all other rounds for this tournament.`,
        confirmButtonText: 'Mark Active',
        cancelButtonText: 'Cancel',
        confirmButtonClass: 'btn-primary',
        data: {
          type: 'toggleRoundActive',
          roundId: round.roundId,
          makeActive: true
        }
      });
      return;
    }
    const updatedRound = this.applyRoundActiveState(round.roundId, false);
    if (updatedRound) {
      this.saveRound(updatedRound);
    }
  }

  private onToggleRoundActiveConfirmed(data: any): void {
    const roundId = data?.roundId as string | undefined;
    const makeActive = data?.makeActive as boolean | undefined;

    if (!roundId || makeActive !== true) {
      return;
    }

    const updatedRound = this.applyRoundActiveState(roundId, true);
    if (updatedRound) {
      this.saveRound(updatedRound);
    }
  }

  private applyRoundActiveState(roundId: string, newActiveState: boolean): Round | null {
    const existingRounds = this.rounds();
    const targetRound = existingRounds.find(r => r.roundId === roundId);

    if (!targetRound) {
      return null;
    }

    let hasChanges = false;
    let updatedTargetRound: Round | null = null;

    const updatedRounds = existingRounds.map(r => {
      if (r.roundId === roundId) {
        if (r.active === newActiveState) {
          updatedTargetRound = r;
          return r;
        }

        hasChanges = true;
        const nextRound = { ...r, active: newActiveState };
        updatedTargetRound = nextRound;
        return nextRound;
      }

      if (newActiveState && r.active) {
        hasChanges = true;
        return { ...r, active: false };
      }

      return r;
    });

    if (!hasChanges) {
      return updatedTargetRound;
    }

    this.rounds.set(updatedRounds);

    const updatedRound = updatedTargetRound ?? updatedRounds.find(r => r.roundId === roundId) ?? null;
    if (updatedRound) {
      this.tournamentService.selectedRound = updatedRound;
    }

    this.markDirty();

    return updatedRound;
  }

  // Toggle round official status
  toggleRoundOfficial(round: Round): void {
    if (!round.roundId) return;

    // Update the round's official status
    const updatedRounds = this.rounds().map(r => {
      if (r.roundId === round.roundId) {
        r.official = !r.official;
      }
      return r;
    });

    // Update the rounds signal
    this.rounds.set(updatedRounds);

    // Update the selected round in the service with the updated version
    const updatedRound = updatedRounds.find(r => r.roundId === round.roundId);
    if (updatedRound) {
      this.tournamentService.selectedRound = updatedRound;
    }

    // Mark form as dirty
    this.markDirty();
  }

  // Get tournament name or placeholder
  getTournamentName(): string {
    return this.tournamentService.selectedTournamentSignal()?.name || 'No tournament selected';
  }

  // Save round changes
  saveRound(round: Round): void {
    // Get the tournament ID
    const tournamentId = this.tournamentService.selectedTournamentSignal()?.tournamentId;
    if (!tournamentId || !round.roundId) return;

    this.isSaving.set(true);

    const normalizedCutType = round.cutType?.trim();

    // Track this round ID as a local operation to prevent showing notifications (only if suppression is enabled)
    const suppressLocalOperations = this.configService.config?.notifications?.suppressLocalOperations ?? false;
    if (suppressLocalOperations) {
      const timestamps = this.localRoundOperations.get(round.roundId) || [];
      timestamps.push(Date.now());

      // Keep only the last 10 operations per round to prevent memory issues
      if (timestamps.length > 10) {
        timestamps.splice(0, timestamps.length - 10);
      }

      this.localRoundOperations.set(round.roundId, timestamps);
      this.loggingService.debug('RoundsComponent', `Registered local round operation: ${round.roundId}`);
    }

    // Create the update request model
    const updateModel = {
      number: round.number,
      startDate: round.startDate,
      endDate: round.endDate,
      active: round.active,
      official: round.official,
      cutSize: round.cutSize,
      cutType: normalizedCutType && normalizedCutType.length > 0 ? normalizedCutType : undefined,
      resetScores: round.resetScores
    };

    // Call the API to update the round
    this.tournamentService.tournamentsTournamentIdRoundsRoundIdPut(
      tournamentId,
      round.roundId,
      updateModel
    )
      .pipe(
        // After updating, re-fetch the rounds to get the updated data
        switchMap(() => {
          return this.tournamentService.getRounds(tournamentId);
        })
      )
      .subscribe({
        next: (updatedRounds: Round[]) => {
          // Update the rounds signal with the new data
          this.rounds.set(updatedRounds);

          // Find the updated round
          const updatedRound = updatedRounds.find(r => r.roundId === round.roundId);

          // If this was the selected round, update it in the service
          if (updatedRound && this.selectedRound()?.roundId === updatedRound.roundId) {
            this.tournamentService.selectedRound = updatedRound;
          }

          // Reset dirty and saving state after successful save
          this.isDirty.set(false);
          this.isSaving.set(false);

          // Show success message
          this.toastr.success(`Round ${round.number} updated successfully`, 'Success');
        },
        error: (err: any) => {
          this.loggingService.error('Error updating round:', err);
          this.toastr.error(`Failed to update round: ${err.message || 'Unknown error'}`, 'Error');
          this.isSaving.set(false);
        }
      });
  }

  /**
   * Start listening to real-time round events
   */
  private startListeningToRoundEvents(): void {
    // Subscribe to Round Active events
    this.roundEventsSubscription = this.scoringRealtimeService.roundActivity.subscribe({
      next: (event: RoundActivityData) => {
        this.handleRoundEvent(event);
      },
      error: (error) => {
        this.loggingService.error('RoundsComponent', 'Error in round active events subscription:', error);
      }
    });

    // Subscribe to Round Official events  
    this.roundOfficialEventsSubscription = this.scoringRealtimeService.roundOfficialActivity.subscribe({
      next: (event: RoundOfficialActivityData) => {
        this.handleRoundOfficialEvent(event);
      },
      error: (error) => {
        this.loggingService.error('RoundsComponent', 'Error in round official events subscription:', error);
      }
    });

    this.loggingService.debug('RoundsComponent', 'Started listening to real-time round events (Active and Official)');
  }

  /**
   * Stop listening to real-time round events
   */
  private stopListeningToRoundEvents(): void {
    if (this.roundEventsSubscription) {
      this.roundEventsSubscription.unsubscribe();
      this.roundEventsSubscription = undefined;
    }

    if (this.roundOfficialEventsSubscription) {
      this.roundOfficialEventsSubscription.unsubscribe();
      this.roundOfficialEventsSubscription = undefined;
    }

    this.loggingService.debug('RoundsComponent', 'Unsubscribed from real-time round events (Active and Official)');
  }

  /**
   * Handle incoming round events
   */
  private handleRoundEvent(event: RoundActivityData): void {
    this.loggingService.debug('RoundsComponent', 'Processing round event:', event);

    // Find the round in our current rounds list
    const currentRounds = this.rounds();
    const affectedRound = currentRounds.find(r => r.roundId === event.roundId);

    if (!affectedRound) {
      this.loggingService.debug('RoundsComponent', 'Ignoring event for round not in current list');
      return;
    }

    // Check if local operation suppression is enabled
    const suppressLocalOperations = this.configService.config?.notifications?.suppressLocalOperations ?? false;

    if (suppressLocalOperations) {
      // Don't show notification for locally performed operations when suppression is enabled
      const suppressibleOperations = this.configService.config?.notifications?.suppressibleOperations ??
        ['ScorecardUpdated', 'ScorecardMarkedOfficial', 'ScorecardMarkedNotOfficial', 'RoundMarkedActive', 'RoundMarkedNotActive'];
      const suppressionWindowMs = this.configService.config?.notifications?.suppressionWindowMs ?? 2000;

      if (suppressibleOperations.includes(event.type) && this.localRoundOperations.has(event.roundId)) {
        const timestamps = this.localRoundOperations.get(event.roundId)!;
        const now = Date.now();

        // Check if any of the recent operations fall within the suppression window
        const recentOperation = timestamps.find(timestamp => {
          const timeSinceOperation = now - timestamp;
          return timeSinceOperation < suppressionWindowMs;
        });

        if (recentOperation) {
          const timeSinceOperation = now - recentOperation;
          this.loggingService.debug('RoundsComponent', `Local operation found for ${event.type}, time since operation: ${timeSinceOperation} ms`);
          this.loggingService.debug('RoundsComponent', 'Suppressing notification due to recent local operation (suppression enabled)');
          return;
        } else {
          this.loggingService.debug('RoundsComponent', 'No recent local operations within suppression window, showing notification');
        }
      }
    }

    const title = event.type === 'RoundMarkedActive' ? 'Round Marked Active' : 'Round Marked Not Active';
    const message = event.type === 'RoundMarkedActive'
      ? `Round ${affectedRound.number} has been marked active.`
      : `Round ${affectedRound.number} has been marked not active.`;

    try {
      const alertId = event.type === 'RoundMarkedActive'
        ? this.alertService.success(title, message, {
          dismissible: true,
          autoClose: true,
          autoCloseDelay: 30000, // Auto-close after 30 seconds
          category: 'round'
        })
        : this.alertService.warning(title, message, {
          dismissible: true,
          autoClose: true,
          autoCloseDelay: 30000, // Auto-close after 30 seconds
          category: 'round'
        });

      this.loggingService.debug('RoundsComponent', `Round alert notification shown successfully with ID: ${alertId}`);
    } catch (error) {
      this.loggingService.error('RoundsComponent', 'Error showing round alert notification', error);
    }
  }

  /**
   * Handle incoming round official events
   */
  private handleRoundOfficialEvent(event: RoundOfficialActivityData): void {
    this.loggingService.debug('RoundsComponent', 'Processing round official event:', event);

    // Find the round in our current rounds list
    const currentRounds = this.rounds();
    const affectedRound = currentRounds.find(r => r.roundId === event.roundId);

    if (!affectedRound) {
      this.loggingService.debug('RoundsComponent', 'Ignoring official event for round not in current list');
      return;
    }

    // Check if local operation suppression is enabled
    const suppressLocalOperations = this.configService.config?.notifications?.suppressLocalOperations ?? false;

    if (suppressLocalOperations) {
      // Don't show notification for locally performed operations when suppression is enabled
      const suppressibleOperations = this.configService.config?.notifications?.suppressibleOperations ??
        ['ScorecardUpdated', 'ScorecardMarkedOfficial', 'ScorecardMarkedNotOfficial', 'RoundMarkedActive', 'RoundMarkedNotActive', 'RoundMarkedOfficial', 'RoundMarkedNotOfficial'];
      const suppressionWindowMs = this.configService.config?.notifications?.suppressionWindowMs ?? 2000;

      if (suppressibleOperations.includes(event.type) && this.localRoundOperations.has(event.roundId)) {
        const timestamps = this.localRoundOperations.get(event.roundId)!;
        const now = Date.now();

        // Check if any of the recent operations fall within the suppression window
        const recentOperation = timestamps.find(timestamp => {
          const timeSinceOperation = now - timestamp;
          return timeSinceOperation < suppressionWindowMs;
        });

        if (recentOperation) {
          const timeSinceOperation = now - recentOperation;
          this.loggingService.debug('RoundsComponent', `Local round official operation found for ${event.type}, time since operation: ${timeSinceOperation} ms`);
          this.loggingService.debug('RoundsComponent', 'Suppressing official notification due to recent local operation (suppression enabled)');
          return;
        } else {
          this.loggingService.debug('RoundsComponent', 'No recent local official operations within suppression window, showing notification');
        }
      }
    }

    const title = event.type === 'RoundMarkedOfficial' ? 'Round Marked Official' : 'Round Marked Not Official';
    const message = event.type === 'RoundMarkedOfficial'
      ? `Round ${affectedRound.number} has been marked official.`
      : `Round ${affectedRound.number} has been marked not official.`;

    try {
      const alertId = event.type === 'RoundMarkedOfficial'
        ? this.alertService.success(title, message, {
          dismissible: true,
          autoClose: true,
          autoCloseDelay: 30000, // Auto-close after 30 seconds
          category: 'round'
        })
        : this.alertService.info(title, message, {
          dismissible: true,
          autoClose: true,
          autoCloseDelay: 30000, // Auto-close after 30 seconds
          category: 'round'
        });

      this.loggingService.debug('RoundsComponent', `Round official alert notification shown successfully with ID: ${alertId}`);
    } catch (error) {
      this.loggingService.error('RoundsComponent', 'Error showing round official alert notification', error);
    }
  }

}
